/**
 * QualityWidget — analytics-v2 data quality panel (ADS-056 / CORE-961).
 *
 * Prod-only widget — all data sources (Meta CAPI, GA4, DB) are production
 * data only, per plan §6.5. Shows PROD badge like the marketing-lane widgets.
 *
 * Renders a fixed four-surface table (Bph Pixel / Blooph SDK / GA4 web /
 * Firebase mobile) with volume 24h/7d/28d + match quality (Meta only) +
 * Purchase count 7d + reconciliation delta 7d per surface. Below the
 * table: attribution-suppressed order count summary.
 *
 * No sortable columns and no window selector — surfaces are in a fixed
 * order and the columns are fixed lookback buckets. Refetches on the
 * dashboard's manual "Refresh now" via useRefresh().
 *
 * Ramp-up caveat (rampUpNote pattern from CORE-960 R3) is visible during
 * loading + success states, suppressed during error (competing CTA).
 * Copy explains that the numbers are informational until real traffic +
 * ads arrive.
 *
 * Null handling (mirrors backend semantics):
 *   - `volume24h/7d/28d === null` → "—" (source degraded)
 *   - `matchQuality === null` → "—" (Meta EMQ not scored yet, OR GA4 row)
 *   - `purchaseCount7d === null` → "—" (source degraded)
 *   - `dbReconciliationDelta7d === null` → "—" (volume7d=0, or purchaseCount
 *     null, or DB denominator 0)
 */

import { getAnalyticsQuality } from '@/api/client';
import { formatCount, formatDateTime, formatSignedPercent } from '@/utils/format';
import { degradedSourceLabels } from '@/utils/sourceStatuses';
import { useAnalyticsV2Fetch } from '@/hooks/useAnalyticsV2Fetch';
import { attributionCaption } from '@/utils/attribution';
import { AnalyticsV2ErrorBoundary } from './AnalyticsV2ErrorBoundary';
import { WidgetInfoButton } from './WidgetInfoButton';
import type { QualitySurfaceRow } from '@/types/api';

const DESCRIPTION = (
  <>
    <p>
      Ingestion health per data source — always production data.
      Rows are surfaces (Bph Pixel / Blooph SDK / GA4 web / Firebase mobile);
      columns are 24h / 7d / 28d event volume, Meta EMQ score, 7d Purchase
      count, and 7d reconciliation delta vs the DB.
    </p>
    <p>
      <strong>EMQ</strong> (Event Match Quality) is Meta&apos;s 0–10 score
      of how well CAPI events match to Meta users; <code>—</code> until
      sustained volume accumulates. GA4 rows always show <code>—</code>
      (Meta-only field).
    </p>
    <p>
      <strong>Δ vs DB 7d</strong> is signed: negative means the source
      under-reports vs. the DB (typical for GA4 — iOS ATT, ad blockers);
      positive means over-report.
    </p>
    <p>
      Attribution-suppressed orders are reported separately so privacy
      compliance (opted-out / minor buyers) isn&apos;t mistaken for data loss.
    </p>
  </>
);

/** Match quality is a 0-10 score. Render at 1dp; null → "—". */
function formatMatchQuality(value: number | null): string {
  return value === null ? '—' : value.toFixed(1);
}

const RAMP_UP_NOTE = (
  <>
    <strong>Early window — informational only.</strong> No ad traffic yet
    and mobile app updates propagate over weeks. Meta EMQ scores need
    sustained CAPI event volume to be meaningful; reconciliation delta
    stabilises as ingestion coverage matures. Use this panel to spot
    outright pipe failures, not to draw conclusions about ranking accuracy.
  </>
);

export function QualityWidget(): JSX.Element {
  return (
    <AnalyticsV2ErrorBoundary title="Data Quality" resetKey={0}>
      <QualityWidgetBody />
    </AnalyticsV2ErrorBoundary>
  );
}

function QualityWidgetBody(): JSX.Element {
  const { data, error, isLoading } = useAnalyticsV2Fetch(
    () => getAnalyticsQuality(),
    [],
  );

  const degraded = data === null ? [] : degradedSourceLabels(data.sourceStatuses);

  return (
    <section className="quality-widget">
      <header className="quality-widget-header">
        <h3>
          Data Quality
          <span className="analytics-v2-widget-prod-badge" aria-hidden="true" aria-label="Production data only">
            PROD
          </span>
          <WidgetInfoButton widgetName="Data Quality" description={DESCRIPTION} />
        </h3>
      </header>

      {/* Ramp-up caveat is visible during loading and success — primer
          before the operator sees numbers. Suppressed on error (competes
          with the error CTA). */}
      {error === null && (
        <div className="quality-widget-rampup-note" role="note">
          {RAMP_UP_NOTE}
        </div>
      )}

      {isLoading && <p className="quality-widget-loading">Loading quality data…</p>}

      {!isLoading && error !== null && (
        <div className="quality-widget-error" role="alert">
          Could not load quality data: {error}
        </div>
      )}

      {!isLoading && error === null && data !== null && (
        <>
          <p className="quality-widget-caption">
            Attribution: {attributionCaption(data.attributionWindow)}. Last updated{' '}
            {formatDateTime(data.lastUpdated)}.
          </p>

          {degraded.length > 0 && (
            <p className="quality-widget-degradation" role="status">
              {degraded.join(', ')} unavailable — some cells may show dashes.
            </p>
          )}

          <div className="table-wrap">
            <table className="quality-widget-table">
              <thead>
                <tr>
                  <th>Surface</th>
                  <th className="quality-widget-num">Vol 24h</th>
                  <th className="quality-widget-num">Vol 7d</th>
                  <th className="quality-widget-num">Vol 28d</th>
                  <th className="quality-widget-num">EMQ</th>
                  <th className="quality-widget-num">Purchases 7d</th>
                  <th className="quality-widget-num">Δ vs DB 7d</th>
                </tr>
              </thead>
              <tbody>
                {data.surfaces.map((row) => (
                  <SurfaceRow key={row.surface} row={row} />
                ))}
              </tbody>
            </table>
          </div>

          <p className="quality-widget-suppressed">
            <strong>Attribution-suppressed orders (7d):</strong>{' '}
            <span className="quality-widget-suppressed-value">
              {formatCount(data.attributionSuppressedCount7d)}
            </span>{' '}
            <span className="quality-widget-suppressed-note">
              — orders whose buyer opted out of sharing (do_not_sell_or_share = true)
              or is flagged as a minor (is_minor = true). Reported explicitly so
              privacy compliance is not mistaken for data loss.
            </span>
          </p>
        </>
      )}
    </section>
  );
}

function SurfaceRow({ row }: { row: QualitySurfaceRow }): JSX.Element {
  return (
    <tr>
      <td>{row.displayName}</td>
      <td className="quality-widget-num">{formatCount(row.volume24h)}</td>
      <td className="quality-widget-num">{formatCount(row.volume7d)}</td>
      <td className="quality-widget-num">{formatCount(row.volume28d)}</td>
      <td className="quality-widget-num">{formatMatchQuality(row.matchQuality)}</td>
      <td className="quality-widget-num">{formatCount(row.purchaseCount7d)}</td>
      <td className="quality-widget-num">{formatSignedPercent(row.dbReconciliationDelta7d)}</td>
    </tr>
  );
}

